import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from 'components/Accordion';
import Button from 'components/DesignSystem/Button';
import DatePicker from 'components/DatePicker';
import RadioButton from 'components/DesignSystem/RadioButton';
import CalendarioLegenda from 'components/Relatorios/shared/Aprovacao/CalendarioLegenda';
import useCalendarioAprovacao from 'hooks/Relatorios/Aprovacao/useCalendarioAprovacao';
import {
  selectSemanalDataAnalisada,
  selectSemanalCenario,
  setDataAnalisada,
  setCenario,
} from 'redux/reducers/relatoriosSemanalReducer';
import { getAprovacaoConfig } from 'helper/Relatorios/Aprovacao/config';
import { SemanalCenario } from 'types/Relatorios/Semanal';

import './index.scss';

const config = getAprovacaoConfig('semanal');

const CENARIOS: { value: SemanalCenario; label: string }[] = (
  Object.keys(config.variante.labels) as SemanalCenario[]
).map((value) => ({ value, label: config.variante.labels[value] }));

/**
 * Painel de controle do Informe Semanal. Espelha o `IntersemanalControlPanel`
 * (mesmo padrão do Figma: Accordion do design system + DatePicker inline para
 * a Data Analisada, radios fechados Early/Preliminar/POS_ONS para o Cenário).
 * Rascunho local (useState) só faz commit ao slice no clique em "Atualizar Data".
 *
 * Com a aprovação habilitada, o calendário ganha bolinhas (admin) ou só permite dias
 * publicados (cliente), consultando o **cenário aplicado**. D8: a data do rascunho
 * acompanha a data aplicada; o cenário do rascunho não é ressincronizado.
 */
export default function SemanalControlPanel() {
  const dispatch = useDispatch();
  const dataAnalisada = useSelector(selectSemanalDataAnalisada);
  const cenarioAplicado = useSelector(selectSemanalCenario);

  const [draftData, setDraftData] = useState(
    () => new Date(dataAnalisada.ano, dataAnalisada.mes - 1, dataAnalisada.dia),
  );
  const [draftCenario, setDraftCenario] = useState<SemanalCenario>(cenarioAplicado);

  const {
    datePickerProps, mostraLegenda, onDataSelecionada,
  } = useCalendarioAprovacao(config, dataAnalisada, cenarioAplicado);

  const { ano, mes, dia } = dataAnalisada;
  useEffect(() => {
    setDraftData((atual) => (
      atual.getFullYear() === ano && atual.getMonth() === mes - 1 && atual.getDate() === dia
        ? atual
        : new Date(ano, mes - 1, dia)
    ));
  }, [ano, mes, dia]);

  const handleDataChange = useCallback((date: Date | null) => {
    if (!date) return;
    setDraftData(date);
    onDataSelecionada(date);
  }, [onDataSelecionada]);

  const handleCenarioChange = useCallback((value: SemanalCenario) => () => {
    setDraftCenario(value);
  }, []);

  const handleAtualizar = useCallback(() => {
    dispatch(setDataAnalisada({
      mes: draftData.getMonth() + 1,
      ano: draftData.getFullYear(),
      dia: draftData.getDate(),
    }));
    dispatch(setCenario(draftCenario));
  }, [dispatch, draftData, draftCenario]);

  return (
    <div className="semanal-control-panel">
      <Accordion defaultActiveKey="0">
        <Accordion.Item eventKey="0">
          <Accordion.Header>Data Analisada</Accordion.Header>
          <Accordion.Body className="d-grid">
            <DatePicker
              inline
              selected={draftData}
              onChange={handleDataChange}
              maxDate={new Date()}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...datePickerProps}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>Cenário</Accordion.Header>
          <Accordion.Body className="d-grid gap-1">
            {CENARIOS.map(({ value, label }) => (
              <RadioButton
                key={value}
                name="semanal-cenario"
                label={label}
                value={value}
                checked={draftCenario === value}
                onChange={handleCenarioChange(value)}
              />
            ))}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Button onClick={handleAtualizar} className="semanal-control-panel__atualizar">
        Atualizar Data
      </Button>

      {mostraLegenda && <CalendarioLegenda isAdmin />}
    </div>
  );
}
